import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { apiError } from "./api";
import { useAuth } from "./QcApp";

const COMPANY_FIELDS = [
  ["applicant_name", "Applicant Name"],
  ["applicant_pan", "Applicant PAN"],
  ["applicant_address", "Applicant Address"],
  ["our_bank_full", "Our Bank (single line, Form A2)"],
  ["our_bank_name", "Bank Name (Bank Letter)"],
  ["our_bank_branch", "Bank Branch"],
  ["our_bank_city", "Bank City"],
  ["our_account_no", "Debit Account No."],
  ["place", "Place (FEMA signing location)"],
];

export default function SettingsPage() {
  const { user, refresh } = useAuth();
  const roles = user.roles || [];
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [company, setCompany] = useState(null);
  const [sigBusy, setSigBusy] = useState(false);

  useEffect(() => {
    api.get("/settings/company").then((r) => setCompany(r.data)).catch(() => {});
  }, []);

  const changePw = async () => {
    if (next !== confirm) return toast.error("New passwords do not match");
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password changed");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveCompany = async () => {
    try {
      await api.put("/settings/company", company);
      toast.success("Company profile saved");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const uploadSig = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSigBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/users/me/signature", fd);
      toast.success("Signature saved — it will appear on future Bill Passed stamps");
      refresh();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSigBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="qc-settings-page">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#8A9A91]">Account</p>
        <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight">Settings</h1>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-[#E8E6E1] bg-white p-6">
          <h2 className="mb-4 font-display text-lg font-semibold">Change my password</h2>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Current password</Label>
              <Input data-testid="qc-settings-current-pw" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>New password</Label>
              <Input data-testid="qc-settings-new-pw" type="password" value={next} onChange={(e) => setNext(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Confirm new password</Label>
              <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
            </div>
            <Button data-testid="qc-settings-change-pw" onClick={changePw} className="rounded-full bg-[#1C3F2B] text-[#F9F8F6] hover:bg-[#26543a]">
              Change password
            </Button>
          </div>
        </div>

        {(roles.includes("unit_head") || roles.includes("admin")) && (
          <div className="rounded-2xl border border-[#E8E6E1] bg-white p-6">
            <h2 className="mb-2 font-display text-lg font-semibold">Scanned signature</h2>
            <p className="mb-4 text-sm text-[#8A9A91]">
              Uploaded once and placed on the Bill Passed stamp (PASSED BY) whenever you pass a domestic bill to Accounts.
            </p>
            <p className="mb-3 text-sm">
              Status: {user.has_signature ? <span className="font-semibold text-[#1C3F2B]">On file</span> : <span className="font-semibold text-[#7a5b12]">Not uploaded</span>}
            </p>
            <Input data-testid="qc-settings-signature" type="file" accept="image/*" onChange={uploadSig} disabled={sigBusy} />
          </div>
        )}
      </div>

      {company && (
        <div className="rounded-2xl border border-[#E8E6E1] bg-white p-6">
          <h2 className="mb-1 font-display text-lg font-semibold">Company profile (bank document settings)</h2>
          <p className="mb-4 text-sm text-[#8A9A91]">
            Fixed values printed on Form A2, the Bank Letter and the FEMA declaration. Only Admin can edit.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            {COMPANY_FIELDS.map(([k, label]) => (
              <div key={k} className={`space-y-1 ${k.includes("address") || k === "our_bank_full" || k === "applicant_name" ? "sm:col-span-2" : ""}`}>
                <Label className="text-xs">{label}</Label>
                <Input
                  data-testid={`qc-company-${k}`}
                  value={company[k] || ""}
                  disabled={!roles.includes("admin")}
                  onChange={(e) => setCompany({ ...company, [k]: e.target.value })}
                />
              </div>
            ))}
          </div>
          {roles.includes("admin") && (
            <Button data-testid="qc-company-save" onClick={saveCompany} className="mt-4 rounded-full bg-[#1C3F2B] text-[#F9F8F6] hover:bg-[#26543a]">
              Save company profile
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
